import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useEffect, useState } from 'react';
import bookingService, { AppointmentType } from '@/services/bookingService';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';

// Color options for appointment types
const COLOR_OPTIONS = [
  { value: '#0D9488', label: 'Teal' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#F97316', label: 'Orange' },
  { value: '#22C55E', label: 'Green' },
  { value: '#EAB308', label: 'Yellow' },
  { value: '#6B7280', label: 'Gray' },
];

// Duration options
const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
];

interface props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isEditing: boolean;
  appointmentType?: AppointmentType;
  onSuccess: () => void;
}

export interface AppointmentTypeFormData {
  name: string;
  description: string;
  duration_minutes: number;
  price_cents: number | null;
  color: string;
  telehealth_enabled: boolean;
  in_person_enabled: boolean;
  is_public: boolean;
  is_active: boolean;
}

const AppointmentTypeDialog = ({
  open,
  onOpenChange,
  isEditing,
  onSuccess,
  appointmentType,
}: props) => {
  const { clinic } = useClinic();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [priceDisplay, setPriceDisplay] = useState('');
  const [appointmentTypeForm, setAppointmentTypeForm] = useState<AppointmentTypeFormData>({
    name: appointmentType?.name || '',
    description: appointmentType?.description || '',
    duration_minutes: appointmentType?.duration_minutes || 30,
    price_cents: appointmentType?.price_cents || null,
    color: appointmentType?.color || '#0D9488',
    telehealth_enabled: appointmentType?.telehealth_enabled || true,
    in_person_enabled: appointmentType?.in_person_enabled || false,
    is_public: appointmentType?.is_public || true,
    is_active: appointmentType?.is_active || true,
  });

  // Save appointment type (create or update)
  const handleSaveAppointmentType = async () => {
    if (!clinic?.id) return;

    if (!appointmentTypeForm.name) {
      toast({
        title: 'Validation Error',
        description: 'Please enter an appointment type name.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await bookingService.updateAppointmentType(
          clinic.id,
          appointmentType.id,
          appointmentTypeForm
        );
        toast({
          title: 'Success',
          description: 'Appointment type updated',
        });
      } else {
        await bookingService.createAppointmentType(clinic.id, appointmentTypeForm);
        toast({
          title: 'Success',
          description: 'Appointment type created',
        });
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to save appointment type:', error);
      toast({
        title: 'Error',
        description: 'Failed to save appointment type',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (appointmentType) {
      setAppointmentTypeForm({
        name: appointmentType.name || '',
        description: appointmentType.description || '',
        duration_minutes: appointmentType.duration_minutes || 30,
        price_cents: appointmentType.price_cents || null,
        color: appointmentType.color || '#0D9488',
        telehealth_enabled: appointmentType.telehealth_enabled ?? true,
        in_person_enabled: appointmentType.in_person_enabled ?? false,
        is_public: appointmentType.is_public ?? true,
        is_active: appointmentType.is_active ?? true,
      });
      setPriceDisplay(
        appointmentType.price_cents ? (appointmentType.price_cents / 100).toFixed(2) : ''
      );
    } else {
      setAppointmentTypeForm({
        name: '',
        description: '',
        duration_minutes: 30,
        price_cents: null,
        color: '#0D9488',
        telehealth_enabled: true,
        in_person_enabled: false,
        is_public: true,
        is_active: true,
      });
      setPriceDisplay('');
    }
  }, [open, appointmentType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] max-w-lg overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Appointment Type' : 'New Appointment Type'}</DialogTitle>
          <DialogDescription>Configure the details for this appointment type</DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          {/* Name */}
          <div className='space-y-2'>
            <Label htmlFor='apt-name'>Name *</Label>
            <Input
              id='apt-name'
              value={appointmentTypeForm.name}
              onChange={e =>
                setAppointmentTypeForm({ ...appointmentTypeForm, name: e.target.value })
              }
              placeholder='e.g., Initial Consultation'
            />
          </div>

          {/* Description */}
          <div className='space-y-2'>
            <Label htmlFor='apt-description'>Description</Label>
            <Textarea
              id='apt-description'
              value={appointmentTypeForm.description}
              onChange={e =>
                setAppointmentTypeForm({ ...appointmentTypeForm, description: e.target.value })
              }
              placeholder='Brief description of this appointment type...'
              rows={2}
            />
          </div>

          {/* Duration */}
          <div className='space-y-2'>
            <Label>Duration</Label>
            <Select
              value={appointmentTypeForm.duration_minutes.toString()}
              onValueChange={v =>
                setAppointmentTypeForm({
                  ...appointmentTypeForm,
                  duration_minutes: parseInt(v),
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Visit Fee */}
          <div className='space-y-2'>
            <Label htmlFor='apt-price'>Visit Fee ($)</Label>
            <Input
              id='apt-price'
              type='number'
              min='0'
              step='0.01'
              value={priceDisplay}
              onChange={e => setPriceDisplay(e.target.value)}
              onBlur={() => {
                const parsed = parseFloat(priceDisplay);
                if (priceDisplay === '' || isNaN(parsed)) {
                  setAppointmentTypeForm({ ...appointmentTypeForm, price_cents: null });
                  setPriceDisplay('');
                } else {
                  const cents = Math.round(parsed * 100);
                  setAppointmentTypeForm({ ...appointmentTypeForm, price_cents: cents });
                  setPriceDisplay((cents / 100).toFixed(2));
                }
              }}
              placeholder='0.00'
            />
            <p className='text-xs text-slate-500'>Leave empty for free visits</p>
          </div>

          {/* Color */}
          <div className='space-y-2'>
            <Label>Color</Label>
            <div className='flex flex-wrap gap-2'>
              {COLOR_OPTIONS.map(color => (
                <button
                  key={color.value}
                  type='button'
                  onClick={() =>
                    setAppointmentTypeForm({ ...appointmentTypeForm, color: color.value })
                  }
                  className={cn(
                    'h-8 w-8 rounded-full border-2 transition-all',
                    appointmentTypeForm.color === color.value
                      ? 'scale-110 border-slate-900'
                      : 'border-transparent hover:scale-105'
                  )}
                  style={{ backgroundColor: color.value }}
                  title={color.label}
                />
              ))}
            </div>
          </div>

          {/* Meeting Type */}
          <div className='space-y-3'>
            <Label>Meeting Type</Label>
            <div className='flex flex-col gap-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm'>Video Call</span>
                <Switch
                  checked={appointmentTypeForm.telehealth_enabled}
                  onCheckedChange={checked =>
                    setAppointmentTypeForm({
                      ...appointmentTypeForm,
                      telehealth_enabled: checked,
                    })
                  }
                />
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm'>In-Person</span>
                <Switch
                  checked={appointmentTypeForm.in_person_enabled}
                  onCheckedChange={checked =>
                    setAppointmentTypeForm({
                      ...appointmentTypeForm,
                      in_person_enabled: checked,
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Public Toggle */}
          <div className='flex items-center justify-between pt-2'>
            <div>
              <Label>Public</Label>
              <p className='text-sm text-slate-500'>Allow patients to book this type</p>
            </div>
            <Switch
              checked={appointmentTypeForm.is_public}
              onCheckedChange={checked =>
                setAppointmentTypeForm({ ...appointmentTypeForm, is_public: checked })
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAppointmentType}
            disabled={!appointmentTypeForm.name || saving}
            className='bg-teal-600 hover:bg-teal-700'
          >
            {saving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentTypeDialog;
